











































































































import { Component, Emit, Prop, PropSync, Vue, Watch } from 'vue-property-decorator';
import { Correction, Vat, UsersApi, User } from '@/api';
import { ParsingUtil } from '@/common/ParsingUtil';
import debounce from 'lodash.debounce';
import _ from 'lodash';
import { VatCorrectionService } from '@/services/vat-correction-service';
import { CustomFormulaResult, Millimeters } from '@/typings/custom-formula-calculator';
import Toast from '@/components/display/toast.vue';
import { ApiUtils } from '@/common/ApiUtils';

@Component({
	methods: { debounce },
	components: {},
})
export default class MilkVatCorrectionSettings extends Vue {
	public readonly smallScreen = window.innerWidth;
	public calculationError: string | null = null;

	@Prop({ required: true, type: Object })
	public vat!: Vat;

	@PropSync('savedReferences')
	public _savedReferences!: Correction[];

	@PropSync('parameters')
	public _parameters!: Record<string, string | number>;

	@PropSync('correction')
	public _correction!: Record<string, number | null>;

	public fields: any[] = [];

	public calculatedVat: CustomFormulaResult | Error | null = null;

	public vatVolumes: {
		label: string;
		field: string;
	}[] = this.buildVatVolumes();

	public user: User | null = null;
	private readonly userApi = new UsersApi();
	private readonly vatService: VatCorrectionService = new VatCorrectionService();
	private toast: Toast = new Toast();

	public formatLitres(value: number) {
		return this.$options.filters?.numeric(value) + ' L';
	}

	public calculateTotal(field: string): number {
		return (
			ParsingUtil.parseInt(this._correction[field]) + ParsingUtil.parseInt(this._parameters[field])
		);
	}

	public buildVatVolumes(): {
		label: string;
		field: string;
	}[] {
		return [
			{
				label: 'Nominal',
				field: this.formatLitres(this.vat.size),
			},
			{
				label: 'Max',
				field: this.formatLitres(this.calculatedVat?.volume),
			},
		];
	}

	public created() {
		this.onVatChanged()
	}

	@Watch('vat', { immediate: true, deep: true })
	public async onVatChanged() {
		this.fields =
			(<(object & { inputs: any }) | null>this.vat?.customformula)?.inputs
				?.replace(/(\r\n|\n|\r)/g, '\\n')
				?.split('\\n')
				?.map((input: any) => {
					const [type, name, label, parameters] = input.split('|');
					return { type, name, label, parameters: parameters?.split(',') };
				}) ?? [];
		await this.updateVat();
		if (this.vat.currentcorrection != null)
			await this.getUser(this.vat.currentcorrection.user_id);
	}

	public async getUser(userId: number) {
		try {
			const userResponse = (await this.userApi.getUserId(userId))?.data
			this.user = userResponse as User;
		} catch (e) {
			const { errorMessage } = ApiUtils.handleApiError(
				e,
				`Error ${e.response.status} loading user details for the notified user for sent text ID: ${sentTextId}`,
			);
			this.toast.makeToast(errorMessage, 'Error', 'danger', true);
		}
	}

	public async updateCorrections(e: Event): Promise<void> {
		e.preventDefault();
		if (!this.vat?.id) return;
		const { errorMessage } = await this.vatService.setCorrection(
			this.vat.id,
			this._correction,
		);

		if (errorMessage) {
			this.toast.makeToast(errorMessage, 'Error', 'error');
			return;
		}

		this.toast.makeToast('Updated formula corrections', 'Success', 'success');
	}

	public setCorrectionListDebounced = _.debounce(this.setCorrectionList, 20);
	public async setCorrectionList(): Promise<void> {
		this.onUpdateCorrection();
	}

	public async updateVat(): Promise<void> {
		let additionalVatParams: any = {
			emptyDistance: this.vat.emptyDistance,
			volumeCalibration: this.vat.volumeCalibration,
		};
		if (additionalVatParams.emptyDistance == undefined) {
			delete additionalVatParams.emptyDistance;
		}

		const calculation = await VatCorrectionService.calculateMilkVatVolume(
			[<Millimeters>100],
			additionalVatParams,
			this.vat ?? null,
			this._parameters,
			this._correction,
		);
		this.calculationError = calculation?.error ?? null;
		this.calculatedVat = calculation?.result ?? null;
		this.vatVolumes = this.buildVatVolumes();
	}

	@Emit('updateCorrection')
	public onUpdateCorrection(): MilkVatCorrectionSettings {
		return this;
	}

	public formatFormulaError() {
		return (
			this.calculationError?.substring(0, this.calculationError?.indexOf('Formula =') ?? 0) ??
			'Unknown error'
		).trim();
	}
}
